// Фигура спереди и сзади с подсветкой групп мышц. Контуры — Open-Muscle-Map (MIT, LICENSE рядом),
// грузятся отдельным чанком при первом показе; цвета — CSS-переменные --muscle-* из index.css.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/cn'

import { GROUP_OF_FIGURE, TONE_LABELS, type MuscleTone, type Sex } from './muscles'

const loaders: Record<Sex, () => Promise<{ default: string }>> = {
  male: () => import('./male.svg?raw'),
  female: () => import('./female.svg?raw'),
}
const loaded: Partial<Record<Sex, string>> = {}

function useFigure(sex: Sex): string | null {
  const [, setReady] = useState(0)
  useEffect(() => {
    if (loaded[sex]) return
    let alive = true
    void loaders[sex]().then((mod) => {
      loaded[sex] = mod.default
      if (alive) setReady((n) => n + 1)
    })
    return () => {
      alive = false
    }
  }, [sex])
  return loaded[sex] ?? null
}

export function MuscleMap({
  sex,
  tones,
  muscleFill,
  selected,
  onSelect,
  label,
  className,
  frontOnly = false,
}: {
  sex: Sex
  /** Группа мышц (chest, quads…) → тон; без тона — нейтральная. */
  tones?: Record<string, MuscleTone | undefined>
  /** Цвет отдельного контура по его ID и координате начала в SVG. */
  muscleFill?: (id: string, originX: number) => string | undefined
  selected?: string | null
  onSelect?: (group: string) => void
  /** Подпись для скринридера: что показывает фигура. */
  label: string
  className?: string
  frontOnly?: boolean
}) {
  const svg = useFigure(sex)
  const ref = useRef<HTMLDivElement>(null)
  const html = useMemo(() => ({ __html: svg ?? '' }), [svg])

  useLayoutEffect(() => {
    const root = ref.current
    if (!root || !svg) return
    // В исходных SVG передняя и задняя фигуры занимают по половине поля.
    root.querySelector('svg')?.setAttribute('viewBox', frontOnly ? '34 38 591 1133' : '34 38 1182 1133')
    for (const g of root.querySelectorAll<SVGGElement>('[data-muscle-id]')) {
      const id = g.dataset.muscleId ?? ''
      if (muscleFill) {
        const originX = Number(g.getAttribute('transform')?.match(/^translate\((\d+) /)?.[1])
        g.style.setProperty('--muscle-fill', muscleFill(id, originX) ?? '')
      }
      const group = GROUP_OF_FIGURE[id]
      if (!group) continue
      g.dataset.key = group
      const tone = tones?.[group]
      if (tone) g.dataset.tone = tone
      else delete g.dataset.tone
      if (group === selected) g.dataset.selected = ''
      else delete g.dataset.selected
    }
  }, [svg, tones, muscleFill, selected, frontOnly])

  return (
    <figure className={cn('m-0', className)}>
      <div
        ref={ref}
        role="img"
        aria-label={label}
        className={cn('muscle-map w-full', frontOnly ? 'aspect-[591/1133]' : 'aspect-[1182/1133]')}
        data-selectable={onSelect ? '' : undefined}
        onClick={(event) => {
          const group = (event.target as Element).closest<SVGGElement>('[data-key]')?.dataset.key
          if (group && onSelect) onSelect(group)
        }}
        dangerouslySetInnerHTML={html}
      />
      {frontOnly ? null : (
        <figcaption className="mt-1 grid grid-cols-2 text-center text-xs tracking-wide text-muted">
          <span>Спереди</span>
          <span>Сзади</span>
        </figcaption>
      )}
    </figure>
  )
}

export function MuscleLegend({ tones, className }: { tones: readonly MuscleTone[]; className?: string }) {
  return (
    <p className={cn('flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-muted', className)}>
      {tones.map((tone) => (
        <span key={tone} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="size-3 rounded"
            style={{
              background: tone === 'planned' ? 'var(--muscle-primary)' : `var(--muscle-${tone})`,
              opacity: tone === 'idle' ? 0.6 : tone === 'planned' ? 0.3 : 1,
            }}
          />
          {TONE_LABELS[tone]}
        </span>
      ))}
    </p>
  )
}
